import {
  Box,
  Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Backdrop,
  CircularProgress,
  Typography,
  Tooltip,
  DialogActions,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { MdModeEdit, MdOutlineClose } from "react-icons/md";
import axios from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { useNotification } from "../../../../hooks/useNotification";
import useStyles from "../../../../themes/style.js";
import NoDataFound from "../../../../components/NoDataFound";
import { IoAddCircle } from "react-icons/io5";
import { IoMdCheckmark } from "react-icons/io";

const ContractDetails = ({ userDetails, onUpdate }) => {
  const classes = useStyles();
  const token = localStorage.getItem("token");
  const [loader, setLoader] = useState(false);
  const [enableEdit, setEnableEdit] = useState(false);

  const handleNotificationCall = useNotification();
  const [showAddField, setShowAddField] = useState(false);
  const [editContractData, setEditContractData] = useState(false);

  const validationSchema = Yup.object().shape({
    isContract: Yup.boolean()
      .required("This field is required")
      .oneOf([true, false], "Invalid value for isContract"),
    contractClientName: Yup.string().nullable().notRequired(),
    contractStartDate: Yup.string().when([], {
      is: () =>
        userDetails.isContract === true &&
        userDetails.employee_contracts.length === 0,
      then: Yup.string().required("Contract Start Date is required"),
      otherwise: Yup.string(),
    }),
    contractEndDate: Yup.string().when([], {
      is: () =>
        userDetails.isContract === true &&
        userDetails.employee_contracts.length === 0,
      then: Yup.string().required("Contract End Date is required"),
      otherwise: Yup.string(),
    }),
    contractType: Yup.string().nullable().notRequired(),
    contractDetails: Yup.string().nullable().notRequired(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const isContract = watch("isContract");

  const contractSchema = Yup.object().shape({
    contractClientName: Yup.string().nullable().notRequired(),
    contractType: Yup.string().nullable().notRequired(),
    contractDetails: Yup.string().nullable().notRequired(),
    contractStartDate: Yup.date().required("Contract Start Date is required"),
    contractEndDate: Yup.date()
      .min(Yup.ref("contractStartDate"), "End Date cannot be before Start Date")
      .required("Contract End Date is required"),
  });

  const editContractSchema = Yup.object().shape({
    id: Yup.string().nullable().notRequired(),
    contractClientName: Yup.string().nullable().notRequired(),
    contractType: Yup.string().nullable().notRequired(),
    contractDetails: Yup.string().nullable().notRequired(),
    contractStartDate: Yup.date().required("Contract Start Date is required"),
    contractEndDate: Yup.date()
      .min(Yup.ref("contractStartDate"), "End Date cannot be before Start Date")
      .required("Contract End Date is required"),
  });

  const {
    register: contractRegister,
    handleSubmit: contractHandleSubmit,
    resetField: contractResetField,
    setValue: contractSetValue,
    formState: { errors: contractErrors },
  } = useForm({
    resolver: yupResolver(contractSchema),
  });

  const {
    register: editContractRegister,
    handleSubmit: editContractHandleSubmit,
    setValue: editContractSetValue,
    resetField: editContractResetField,
    formState: { errors: editContractErrors },
  } = useForm({
    resolver: yupResolver(editContractSchema),
  });

  const handleEditContractData = (item) => {
    setEditContractData(true);
    editContractSetValue("id", item.id);
    editContractSetValue("contractType", item.contractType);
    editContractSetValue(
      "contractStartDate",
      moment(item.contractStartDate).format("YYYY-MM-DD"),
    );
    editContractSetValue(
      "contractEndDate",
      moment(item.contractEndDate).format("YYYY-MM-DD"),
    );
    editContractSetValue("contractDetails", item.contractDetails);
  };

  const handleCloseContractModalEdit = () => {
    setEditContractData(false);
    editContractResetField("id");
    editContractResetField("contractType");
    editContractResetField("contractStartDate");
    editContractResetField("contractEndDate");
    editContractResetField("contractDetails");
  };

  const handleEditEmployee = async (values) => {
    const contractDurationData = {
      contractStartDate: values.contractStartDate,
      contractEndDate: values.contractEndDate,
      contractType: values.contractType,
      contractDetails: values.contractDetails,
    };

    setLoader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}admin/editUserContractDetails`,
        {
          ...values,
          userId: userDetails.id,
          contractDurations:
            userDetails?.isContract === true &&
            userDetails?.employee_contracts.length === 0
              ? contractDurationData
              : null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      if (response.data.status) {
        handleNotificationCall("success", response.data.message);
        onUpdate();
        setEnableEdit();
      } else {
        handleNotificationCall("error", response.data.message);
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  };

  const handleEditEmployeeContract = async (values) => {
    setLoader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}employee/editEmployeeContract`,
        {
          userId: userDetails?.id,
          ...values,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      if (response.data.status) {
        handleNotificationCall("success", response.data.message);
        onUpdate();
        handleCloseContractModalEdit();
      } else {
        handleNotificationCall("error", response.data.message);
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  };

  const handleAddContractDetails = async (values) => {
    setLoader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}employee/addEmployeeContract`,
        {
          userId: userDetails?.id,
          ...values,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      if (response.data.status) {
        handleNotificationCall("success", response.data.message);
        onUpdate();
        resetExtendContractPeriod();
      } else {
        handleNotificationCall("error", response.data.message);
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  };

  const resetExtendContractPeriod = () => {
    setShowAddField(false);
    contractResetField("contractType");
    contractResetField("contractStartDate");
    contractResetField("contractEndDate");
    contractResetField("contractDetails");
  };

  return (
    <div>
      {userDetails?.salary_detail && userDetails?.salary_detail.isContract && (
        <>
          <Box>
            <div className="emp-profile-details-table-card">
              <h3 className="gradienttext">Contract Details</h3>
              {!enableEdit ? (
                <button
                  className="emp-profile-edit-button"
                  onClick={() => {
                    contractSetValue(
                      "contractClientName",
                      userDetails.salary_detail?.contractClientName
                        ? userDetails.salary_detail?.contractClientName
                        : "-",
                    );
                    setEnableEdit(!enableEdit);
                  }}
                >
                  <MdModeEdit color="#056dfa" className="svg-icon" />
                  <span className="lable">Edit</span>
                </button>
              ) : (
                <button
                  className="emp-profile-edit-button emp-profile-cancel-button"
                  onClick={() => {
                    setEnableEdit(!enableEdit);
                  }}
                >
                  <MdOutlineClose color="#fa0505" className="svg-icon" />
                  <span className="lable">Cancel</span>
                </button>
              )}

              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "20px",
                  gap: "10px",
                }}
              >
                <Grid container direction="row">
                  <Grid item xs={12}>
                    <p style={{ margin: "10px 0px", fontSize: "16px" }}>
                      Client Name
                    </p>
                    <p style={{ padding: "0" }}>
                      {userDetails.salary_detail?.contractClientName
                        ? userDetails.salary_detail?.contractClientName
                        : "-"}
                    </p>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <TableContainer
                      component={Paper}
                      style={{ width: "100%", margin: "12px 0px" }}
                    >
                      <Table
                        // className={classes.table}
                        aria-label="simple table"
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell align="left">Type</TableCell>
                            <TableCell align="left">Details</TableCell>
                            <TableCell align="right">From Date</TableCell>
                            <TableCell align="right">To Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDetails.employee_contracts.map((row) => (
                            <TableRow
                              style={{ background: "#fff" }}
                              key={row.contractStartDate}
                            >
                              <TableCell align="left">
                                {row.contractType}
                              </TableCell>
                              <TableCell align="left">
                                {row.contractDetails}
                              </TableCell>
                              <TableCell align="right">
                                {moment(row.contractStartDate).format(
                                  "DD MMMM YYYY",
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {moment(row.contractEndDate).format(
                                  "DD MMMM YYYY",
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
                )
              </Box>
            </div>
          </Box>
        </>
      )}

      {!userDetails?.employee_contracts ||
        (userDetails?.employee_contracts.length <= 0 && !isContract && (
          <>
            <NoDataFound height="30vh" message="Employee Not in Contract" />
          </>
        ))}

      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default ContractDetails;
